import { create } from 'zustand';

import type { AnalyticsWorkflowStatus } from '../types/analytics-sync.types';

/** One connector's ingestion workflow inside a sync session — the unit the
 *  progress toast renders a row for and the watcher polls. */
export interface SyncWorkflowState {
  workflowId: string;
  connectorCode: string;
  connectionId: string;
  status: AnalyticsWorkflowStatus;
  /** 0–100, or `null` before the workflow reports progress. */
  percentComplete: number | null;
  error?: string | null;
}

/** A sync session groups the workflows triggered by one click of the button
 *  (one per feeding-source connection). Keyed by experiment id so a second run
 *  can't be launched for the same experiment while one is in flight. */
export interface SyncSession {
  id: string;
  experimentId: string;
  experimentName: string;
  /** Stable Sonner toast id — the watcher updates this toast in place. */
  toastId: string;
  workflows: SyncWorkflowState[];
  startedAt: number;
}

interface AnalyticsSyncState {
  sessions: Record<string, SyncSession>;
}

interface AnalyticsSyncActions {
  /** Register a freshly-triggered session so the app-level watcher starts polling. */
  startSession: (session: SyncSession) => void;
  /** Patch one workflow's polled status/progress within a session. No-op if either id is gone. */
  updateWorkflow: (
    sessionId: string,
    workflowId: string,
    patch: Partial<SyncWorkflowState>,
  ) => void;
  /** Drop a session once every workflow is terminal (the watcher has finalised the toast). */
  endSession: (sessionId: string) => void;
  /** True while a session for this experiment is still registered — gates the trigger button. */
  hasActiveSession: (experimentId: string) => boolean;
}

/**
 * Global analytics-sync store. Lives outside React so the progress toast +
 * polling survive navigation away from the experiment detail page (the
 * app-level `AnalyticsSyncManager` reads `sessions` and owns the toast).
 */
export const useAnalyticsSyncStore = create<AnalyticsSyncState & AnalyticsSyncActions>(
  (set, get) => ({
    sessions: {},

    startSession: (session) => {
      set((state) => ({ sessions: { ...state.sessions, [session.id]: session } }));
    },

    updateWorkflow: (sessionId, workflowId, patch) => {
      set((state) => {
        const session = state.sessions[sessionId];
        if (!session) return state;
        const workflows = session.workflows.map((w) =>
          w.workflowId === workflowId ? { ...w, ...patch } : w,
        );
        return { sessions: { ...state.sessions, [sessionId]: { ...session, workflows } } };
      });
    },

    endSession: (sessionId) => {
      set((state) => {
        if (!(sessionId in state.sessions)) return state;
        const sessions = Object.fromEntries(
          Object.entries(state.sessions).filter(([id]) => id !== sessionId),
        );
        return { sessions };
      });
    },

    hasActiveSession: (experimentId) => experimentId in get().sessions,
  }),
);
